import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Link as LinkIcon, Pencil, UserIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { DashboardWebinar } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import IconNote from '@/components/icons/IconNote';
import { toast } from 'react-toastify';
import { Separator } from '@/components/ui/separator';
import { useTranslation } from 'react-i18next';
import { getFormattedDate } from '@/lib/utils';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';

interface WebinarCardProps {
  webinar: DashboardWebinar;
  variant?: 'full' | 'grid';
}

export const WebinarCard: React.FC<WebinarCardProps> = ({ webinar, variant = 'grid' }) => {
  const navigateAction = useNavigate();
  const userTimezone =
    useAppSelector((state: RootState) => state.workspace.currentWorkspace?.timezone) ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isLive = webinar.status === 'LIVE';
  const isEvergreen = webinar.isEvergreen;

  const cardHeight = variant === 'full' ? 'min-h-[320px]' : 'min-h-[280px]';
  const bgImage = webinar.coverUrl ? `url(${webinar.coverUrl})` : undefined;

  const [timeLeft, setTimeLeft] = useState<string>('');
  const [timeLeftSuffix, setTimeLeftSuffix] = useState<
    'restant' | 'restants' | 'restante' | 'restantes'
  >('restants');
  const dialogJustClosedRef = useRef(false);
  const { t } = useTranslation(['dashboard', 'webinars']);
  useEffect(() => {
    if (isEvergreen || !webinar.startDate) return;
    const tick = () => {
      const startUtc = DateTime.fromISO(webinar.startDate, { zone: 'utc' });
      const nowUtc = DateTime.utc();
      const diff = Math.floor(startUtc.diff(nowUtc, 'seconds').seconds);
      if (diff <= 0) {
        setTimeLeft('LIVE');
      } else if (diff >= 86400) {
        const days = Math.floor(diff / 86400);
        const hours = Math.floor((diff % 86400) / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const parts: string[] = [];
        if (days > 0) parts.push(t('time.day', { count: days }));
        if (hours > 0)
          parts.push(
            `${t('time.hour', { count: hours })}${minutes > 0 ? ` ${t('time.and')} ${t('time.minute', { count: minutes })}` : ''}`,
          );
        setTimeLeft(parts.join(' '));
        setTimeLeftSuffix(
          days === 1
            ? (t('sessionsTable.timeLeft.masculineSingular') as
                | 'restant'
                | 'restants'
                | 'restante'
                | 'restantes')
            : (t('sessionsTable.timeLeft.masculinePlural') as
                | 'restant'
                | 'restants'
                | 'restante'
                | 'restantes'),
        );
      } else {
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        setTimeLeft(
          `${hours > 0 ? t('time.hour', { count: hours }) : ''} ${minutes > 0 ? t('time.minute', { count: minutes }) : ''}${seconds > 0 ? ` ${t('time.second', { count: seconds })}` : ''}`.trim(),
        );
        setTimeLeftSuffix(
          hours === 1 || minutes === 1 || seconds === 1
            ? (t('sessionsTable.timeLeft.feminineSingular') as
                | 'restant'
                | 'restants'
                | 'restante'
                | 'restantes')
            : (t('sessionsTable.timeLeft.femininePlural') as
                | 'restant'
                | 'restants'
                | 'restante'
                | 'restantes'),
        );
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [webinar.startDate, isEvergreen, timeLeft]);

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigateAction(`/webinars/${webinar.id}`);
  };

  const handleJoin = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLive) {
      window.open(
        `/admin-live/${webinar.id}/${webinar.sessionId}`,
        '_blank',
        'noopener,noreferrer',
      );
    } else {
      handleDetails();
    }
  };

  const handleDetails = () => {
    if (dialogJustClosedRef.current) {
      dialogJustClosedRef.current = false;
      return;
    }
    navigateAction(`/webinars/${webinar.id}`);
  };

  return (
    <div
      onClick={handleDetails}
      className={`relative rounded-[32px] overflow-hidden p-8 text-white flex flex-col justify-between ${cardHeight} transition-all duration-300 hover:shadow-2xl cursor-pointer group border border-transparent hover:border-accent/30`}
      style={{
        backgroundImage: bgImage,
        backgroundColor: '#0C0E21',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-linear-to-r from-black/90 via-black/50 to-transparent z-0 pointer-events-none" />

      <div className="relative z-10 flex justify-between items-start">
        <div className="flex items-center gap-2">
          <Badge variant={webinar.type === 'EVERGREEN' ? 'evergreen' : 'live'}>
            {t(`sessionPage.sessionType.${webinar.type.toLowerCase()}`)}
          </Badge>
          <Badge variant={isLive ? 'completed' : 'upcoming'}>
            {isLive ? t('sessionPage.status.inProgress') : t('sessionPage.status.upcoming')}
          </Badge>
        </div>

        <button
          onClick={handleEdit}
          className="p-2 rounded-full bg-white/10 cursor-pointer hover:bg-white/20 backdrop-blur-sm transition text-white"
        >
          <Pencil size={16} />
        </button>
      </div>

      <div className="relative z-10 mt-4">
        <div className="text-4xl  font-semibold tracking-tighter block mb-2">
          {isEvergreen ? t('webinarCard.evergreen.duration') : timeLeft}
          <span className="text-sm font-sans ml-3 font-normal text-white/60">
            {isEvergreen
              ? t('webinarCard.evergreen.suffix')
              : timeLeft === 'LIVE'
                ? ''
                : timeLeftSuffix}
          </span>
        </div>

        <h3 className="text-2xl font-bold mb-1 truncate group-hover:text-accent transition-colors">
          {webinar.title}
        </h3>

        <div className="flex items-center text-sm text-white/70 space-x-3">
          <span>
            {webinar.participantsCount} {t('webinarCard.registrantsLabel')}
          </span>
          {isLive && (
            <>
              <span className="h-3 w-px bg-white/20"></span>
              <span>{t('webinarCard.liveLabel')}</span>
            </>
          )}
        </div>

        {webinar.startDate && (
          <div className="mt-2 text-xs text-white/40 font-medium uppercase tracking-wider">
            {getFormattedDate(webinar.startDate, webinar.timezone || userTimezone)}
          </div>
        )}

        <div className="flex -space-x-2 mt-6">
          {webinar?.presenters?.length > 0 &&
            webinar.presenters.map((presenter) => (
              <div
                key={presenter.id}
                className="size-8 rounded-full border-2 border-[#0C0E21] bg-neutral-800 overflow-hidden flex items-center justify-center"
                title={`${presenter.firstName} ${presenter.lastName}`}
              >
                <span className="text-[15px] font-bold uppercase">{presenter.firstName[0]}</span>
              </div>
            ))}
          {webinar?.moderators?.length > 0 &&
            webinar.moderators.map((moderator) => (
              <div
                key={moderator.id}
                className="size-8 rounded-full border-2 border-[#0C0E21] bg-neutral-800 overflow-hidden flex items-center justify-center"
                title={`${moderator.firstName} ${moderator.lastName}`}
              >
                <span className="text-[15px] font-bold uppercase">{moderator.firstName[0]}</span>
              </div>
            ))}
          {webinar?.speakers?.length > 0 &&
            webinar.speakers.map((speaker) => (
              <div
                key={speaker.id}
                className="size-8 rounded-full border-2 border-[#0C0E21] bg-neutral-800 overflow-hidden flex items-center justify-center"
                title={`${speaker.firstName} ${speaker.lastName}`}
              >
                <span className="text-[15px] font-bold uppercase">{speaker.firstName[0]}</span>
              </div>
            ))}
        </div>
      </div>

      <div className="relative z-10 flex flex-wrap items-center gap-3 mt-6">
        <Button
          onClick={handleJoin}
          className={`px-8 h-12 rounded-full font-bold text-sm transition-all shadow-lg border-none
          ${
            isLive || isEvergreen
              ? 'bg-accent text-dark hover:bg-accent/90'
              : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-md'
          }`}
        >
          {isLive
            ? t('webinarCard.joinLive')
            : isEvergreen
              ? t('webinarCard.launchSession')
              : t('webinarCard.viewDetails')}
        </Button>
        <Dialog
          onOpenChange={(open) => {
            if (!open) dialogJustClosedRef.current = true;
          }}
        >
          <DialogTrigger
            onClick={(e) => e.stopPropagation()}
            className="h-12 px-5 rounded-full border cursor-pointer border-white/20 flex items-center justify-center gap-2 text-white text-sm font-bold hover:bg-white/10 transition backdrop-blur-sm"
          >
            <LinkIcon size={18} />
            {t('webinars:access.links')}
          </DialogTrigger>
          <DialogContent
            onClick={(e) => e.stopPropagation()}
            onPointerDownOutside={() => {
              dialogJustClosedRef.current = true;
            }}
          >
            <DialogHeader>
              <DialogTitle>{t('webinarCard.linksDialog.title')}</DialogTitle>
              <DialogDescription>{t('webinarCard.linksDialog.description')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-subtitle rounded-[20px]">
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="flex gap-2 items-start">
                    <Avatar>
                      <AvatarFallback>
                        <UserIcon size={16} />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-base font-medium text-black">
                        {t('webinarCard.linksDialog.registrationLink')}
                      </p>
                      <a
                        href={webinar?.registrationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary underline underline-offset-2 hover:text-primary-700 break-all min-w-0 flex-1 line-clamp-1"
                      >
                        {webinar?.registrationUrl ?? ''}
                      </a>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 p-1 rounded  transition-colors cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(webinar.registrationUrl ?? '');
                      toast.success(t('webinarCard.linksDialog.linkCopied'));
                    }}
                    title={t('webinarCard.linksDialog.copyLink')}
                  >
                    <IconNote size={24} color="#2c6cff" />
                  </button>
                </div>
              </div>
              <Separator />
              {webinar.speakers.length > 0 || webinar.presenters.length > 0 ? (
                <Accordion
                  type="single"
                  collapsible
                  className="w-full bg-white rounded-[20px] space-y-4"
                  defaultValue="item-0 "
                >
                  {webinar.presenters.map((presenter, index) => (
                    <AccordionItem
                      key={`${presenter.firstName}-${presenter.lastName}-${index}`}
                      value={`presenter-${index}`}
                      className="bg-subtitle"
                    >
                      <AccordionTrigger>
                        <div className="flex gap-2 items-center">
                          <Avatar>
                            {presenter.pictureUrl ? (
                              <AvatarImage
                                src={presenter.pictureUrl}
                                alt={`${presenter.firstName} ${presenter.lastName}`}
                              />
                            ) : null}
                            <AvatarFallback>{presenter.firstName[0]}</AvatarFallback>
                          </Avatar>
                          <p className="text-base font-medium text-black">{`${presenter.firstName} ${presenter.lastName}`}</p>
                          <Badge variant={presenter.role === 'presenter' ? 'live' : 'evergreen'}>
                            {t(`speakersForm.${presenter.role}`)}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {presenter.accessUrl ? (
                          <div className="flex items-center justify-between gap-2 w-full">
                            <a
                              href={presenter.accessUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary underline underline-offset-2 hover:text-primary-700 break-all min-w-0 flex-1 line-clamp-1"
                            >
                              {presenter.accessUrl}
                            </a>
                            <button
                              type="button"
                              className="shrink-0 p-1 rounded  transition-colors cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(presenter.accessUrl ?? '');
                                toast.success(t('webinarCard.linksDialog.linkCopied'));
                              }}
                              title={t('webinarCard.linksDialog.copyLink')}
                            >
                              <IconNote size={24} color="#2c6cff" />
                            </button>
                          </div>
                        ) : (
                          <p className="text-sm text-secondary">
                            {t('webinarCard.linksDialog.noLink')}
                          </p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                  {webinar.speakers.map((speaker, index) => (
                    <AccordionItem
                      key={`${speaker.firstName}-${speaker.lastName}-${index}`}
                      value={`speaker-${index}`}
                      className="bg-subtitle"
                    >
                      <AccordionTrigger>
                        <div className="flex gap-2 items-center">
                          <Avatar>
                            {speaker.pictureUrl ? (
                              <AvatarImage
                                src={speaker.pictureUrl}
                                alt={`${speaker.firstName} ${speaker.lastName}`}
                              />
                            ) : null}
                            <AvatarFallback>{speaker.firstName[0]}</AvatarFallback>
                          </Avatar>
                          <p className="text-base font-medium text-black">{`${speaker.firstName} ${speaker.lastName}`}</p>
                          <Badge variant="finished">{t(`speakersForm.${speaker.role}`)}</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {speaker.accessUrl ? (
                          <div className="flex items-center flex-col  gap-2 w-full">
                            <div className="flex items-center justify-between gap-2 w-full">
                              <p className="text-sm text-secondary break-all min-w-0 flex-1 line-clamp-1">
                                <span className="text-black font-medium">
                                  {t('webinarCard.linksDialog.accessLink')}
                                </span>
                                {speaker.accessUrl}
                              </p>
                              <button
                                type="button"
                                className="shrink-0 p-1 rounded  transition-colors cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(speaker.accessUrl ?? '');
                                  toast.success(t('webinarCard.linksDialog.linkCopied'));
                                }}
                                title={t('webinarCard.linksDialog.copyLink')}
                              >
                                <IconNote size={24} color="#2c6cff" />
                              </button>
                            </div>
                            {speaker.accessCode && (
                              <div className="flex items-center justify-between gap-2 w-full">
                                <p className="text-sm text-secondary break-all min-w-0 flex-1 line-clamp-1">
                                  <span className="text-black font-medium">
                                    {t('webinarCard.linksDialog.accessCode')}
                                  </span>
                                  {speaker.accessCode}
                                </p>
                                <button
                                  type="button"
                                  className="shrink-0 p-1 rounded  transition-colors cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(speaker.accessCode ?? '');
                                    toast.success(t('webinarCard.linksDialog.codeCopied'));
                                  }}
                                  title={t('webinarCard.linksDialog.copyCode')}
                                >
                                  <IconNote size={24} color="#2c6cff" />
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-secondary">
                            {t('webinarCard.linksDialog.noLink')}
                          </p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <p className="text-sm text-secondary">{t('webinarCard.linksDialog.loading')}</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
